import React from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { CurrencyProvider } from './contexts/CurrencyContext';
import { Toaster } from './components/ui/sonner';
import './i18n/i18n';

import Navbar from './components/Navbar';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';
import ScrollToTopOnNavigate from './components/ScrollToTopOnNavigate';
import PurchaseNotification from './components/PurchaseNotification';
import WhatsAppChat from './components/WhatsAppChat';
import FloatingSocialBar from './components/FloatingSocialBar';
import AIChatbot from './components/AIChatbot';
import Landing from './pages/Landing';
import Auth from './pages/Auth';
import AuthCallback from './pages/AuthCallback';
import ResetPassword from './pages/ResetPassword';
import Purchase from './pages/Purchase';
import Dashboard from './pages/Dashboard';
import AffiliateDashboard from './pages/AffiliateDashboard';
// AdminDashboard removed - using Admin component instead
import Admin from './pages/Admin';
import AdminLogin from './pages/AdminLogin';
import About from './pages/About';
import Contact from './pages/Contact';
import FAQ from './pages/FAQ';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import Legal from './pages/Legal';
import Winners from './pages/Winners';
import Partners from './pages/Partners';
import Donate from './pages/Donate';
import Blog from './pages/Blog';
import Prizes from './pages/Prizes';

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-primary"></div>
      </div>
    );
  }

  if (!user && !location.state?.user) {
    return <Navigate to="/auth" replace />;
  }

  return children;
};

const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  // Admin emails list - must match backend ADMIN_EMAILS
  const ADMIN_EMAILS = [
    "admin@marathon-edenlifeintl.org",
    "contact@marathon-edenlifeintl.org",
    "caspaginc@gmail.com"
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }

  // Check if user is admin by email or role
  const isAdmin = ADMIN_EMAILS.map(e => e.toLowerCase()).includes(user.email?.toLowerCase()) || user.role === 'admin';
  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  return children;
};

function AppRouter() {
  const location = useLocation();
  
  // CRITICAL: Check URL fragment (hash) for session_id synchronously during render
  // This prevents race conditions with useEffect timing
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  // Check if we're on the backoffice route - render without navbar/footer
  if (location.pathname === '/backoffice') {
    return (
      <>
        <ScrollToTopOnNavigate />
        <Routes>
          <Route path="/backoffice" element={<AdminLogin />} />
        </Routes>
        <Toaster richColors position="top-right" />
      </>
    );
  }
  
  return (
    <>
      <ScrollToTopOnNavigate />
      <Navbar />
      <div className="min-h-screen">
        <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/auth" element={<Auth />} />
        <Route path="/auth-callback" element={<AuthCallback />} />
        <Route
          path="/purchase"
          element={
            <ProtectedRoute>
              <Purchase />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/affiliate"
          element={
            <ProtectedRoute>
              <AffiliateDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin"
          element={
            <AdminRoute>
              <Admin />
            </AdminRoute>
          }
        />
        <Route path="/about" element={<About />} />
        <Route path="/prizes" element={<Prizes />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/faq" element={<FAQ />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/legal" element={<Legal />} />
        <Route path="/winners" element={<Winners />} />
        <Route path="/partners" element={<Partners />} />
        <Route path="/donate" element={<Donate />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/reset-password" element={<ResetPassword />} />
      </Routes>
      </div>
      <Footer />
      <ScrollToTop />
      <PurchaseNotification />
      <WhatsAppChat />
      <FloatingSocialBar />
      <AIChatbot />
    </>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <LanguageProvider>
            <CurrencyProvider>
              <AppRouter />
              <Toaster position="top-center" richColors />
            </CurrencyProvider>
          </LanguageProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;